<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';

import useWebsiteTexts from '@/composables/useWebsiteTexts';
import useContext from '@/composables/useContext';
import useGiftCard from '@/composables/useGiftCard';
import { ImageSizeHelper } from '@/helpers/image-size';
import { deviceWidths } from '../constants';

import FavoriteButton from '@/components/FavoriteButton.vue';

const props = defineProps({
  product: { type: Object, required: true },
  categoryId: { type: String, default: null },
  productSections: { type: Array, default: () => [] },
  tag: { type: Object, default: null },
  isFavoriteProduct: { type: Boolean, default: false },
  showFavoriteProducts: { type: Boolean, default: false }
});

const { toLitiumMediaUrl } = useContext();
const { websiteText } = useWebsiteTexts();
const { toggleFavoriteProduct } = useGiftCard();

const hover = ref(false);

function onHoverStart(e: PointerEvent) {
  if (e.pointerType === 'mouse') hover.value = true;
}

function onHoverEnd(e: PointerEvent) {
  if (e.pointerType === 'mouse') hover.value = false;
}

const baseImgWidth = typeof window !== 'undefined' ? (window.innerWidth < deviceWidths.phoneMaxWidth ? window.innerWidth : window.innerWidth / 2) : 600;
const cardImgWidth = props.product?.highlighted ? baseImgWidth : baseImgWidth / 2;
const resolvedWidth = ImageSizeHelper.getImageSize(cardImgWidth);

const productUrl = computed(() => {
  if (!props.product?.url) return '';
  if (props.tag?.key && props.tag.value) {
    const sectionQueryParamValue = `${props.tag.key || ''}::${props.tag.value || ''}`;
    return `${props.product.url}?section=${encodeURIComponent(sectionQueryParamValue)}`;
  }
  return props.product.url;
});

function imageUrl(id: string | null | undefined, version?: string | null) {
  if (!id) return '';
  let url = toLitiumMediaUrl(id, { maxWidth: resolvedWidth });
  if (version) url = `${url}&v=${version}`;
  return url;
}

const primaryImageUrl = computed(() =>
  imageUrl(props.product?.primaryImage || props.product?.imageId || props.product?.imageIds?.[0], props.product?.primaryImageVersion)
);

const hoverImageUrl = computed(() => {
  const id = props.product?.secondaryImage;
  if (!id) return '';
  return imageUrl(id, props.product?.secondaryImageVersion);
});

const productName = computed(() => props.product?.productName || props.product?.name || '');
const brandName = computed(() => props.product?.brandName || '');
const comingSoon = computed(() => props.product?.comingSoon ?? false);
const lowImpactFlag = computed(() => props.product?.showLowImpactFlag ?? false);

const colorSwatches = computed<string[]>(() => {
  const raw = props.product?.colors;
  if (!Array.isArray(raw)) return [];
  return raw.filter((c: any) => typeof c === 'string' && c.length > 0);
});
const hasMultipleColors = computed(() => colorSwatches.value.length > 1);

const hasMultipleSizes = computed(() => Boolean(props.product?.hasMultipleSizes));

function onToggleFavorite() {
  // FavoriteButton already does @click.stop.prevent; pass a stub event so
  // toggleFavoriteProduct's preventDefault/stopPropagation calls don't throw.
  toggleFavoriteProduct({ preventDefault() {}, stopPropagation() {} } as Event, props.product);
}
</script>

<template>
  <router-link :to="productUrl" class="product-card group grid no-underline" style="grid-template-rows: subgrid; grid-row: span 2; row-gap: 0">
    <div
      class="product-card__top relative aspect-[3/4] overflow-hidden rounded-sm bg-white"
      @pointerenter="onHoverStart"
      @pointerleave="onHoverEnd"
    >
      <img
        v-if="primaryImageUrl"
        :src="primaryImageUrl"
        :alt="productName"
        loading="lazy"
        :class="['absolute inset-0 h-full w-full object-contain p-[5px] transition-opacity duration-300', hoverImageUrl && hover ? 'opacity-0' : 'opacity-100']"
      />
      <img
        v-if="hoverImageUrl"
        :src="hoverImageUrl"
        :alt="productName"
        loading="lazy"
        :class="['absolute inset-0 h-full w-full object-cover transition-opacity duration-300', hover ? 'opacity-100' : 'opacity-0']"
      />

      <span v-if="lowImpactFlag" class="absolute left-2 top-2 z-10 rounded-sm bg-label px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-white">
        {{ websiteText('common_lowimpact').value }}
      </span>

      <div v-if="comingSoon" class="absolute inset-x-0 top-1/2 z-10 -translate-y-1/2 text-center">
        <span class="inline-block bg-[rgba(84,144,164,0.5)] px-9 py-2 text-xs font-bold uppercase text-white backdrop-blur-sm">
          {{ websiteText('common_comingsoon').value }}
        </span>
      </div>

      <FavoriteButton
        v-if="showFavoriteProducts"
        :is-favorite="isFavoriteProduct"
        size="sm"
        class="absolute right-2 top-2 z-10 p-1"
        :aria-label="websiteText('productdetailspage_favorite').value"
        @toggle="onToggleFavorite"
      />
    </div>

    <div class="product-card__bottom bg-transparent px-1 py-2">
      <p class="m-0 truncate text-[11px] uppercase tracking-wider text-muted-foreground">
        {{ brandName }}
      </p>
      <h3 class="m-0 mt-0.5 line-clamp-2 font-mulish text-[14px] font-normal leading-tight text-foreground">
        {{ productName }}
      </h3>

      <div v-if="hasMultipleColors" class="mt-1.5 flex items-center gap-1.5">
        <span
          v-for="(color, i) in colorSwatches"
          :key="i"
          class="inline-block h-[8.75px] w-[8.75px] rounded-full border"
          :style="{ backgroundColor: color }"
          aria-hidden="true"
        />
      </div>

      <p v-if="hasMultipleSizes" class="m-0 mt-1 text-[11px] text-muted-foreground">
        {{ websiteText('plp_multiplesizes').value }}
      </p>
    </div>
  </router-link>
</template>
